import { Injectable } from '@angular/core'
import { Observable, concatMap, from, last, scan } from 'rxjs'
import { BannersService } from './banners.service'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  BannerModalCode,
  BannerNotificationCode,
  parseBannerModal,
  parseBannerNotification,
} from './libs/banner-notification.lib'
import { BannerSearchParams } from './models/banner-search.model'
import { Banner } from './models/banner.entity'
import { Page } from '../../models/util.model'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class BannersRepository {
  constructor(
    private bannersService: BannersService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search banner and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search banner
   */
  searchBanners$(
    searchParams: BannerSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Banner>> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.bannersService.limit

    return needsPagination
      ? this._paginateBanners$(searchParams)
      : this._searchBanners$(searchParams, returnAll)
  }

  /**
   * Search banners with relative external data
   * @param searchParams - the search params
   * @param fields - the banner fields
   * @param listingData - the already loaded listing data
   * @returns the observable<BannersListingPage> for search banner
   */
  private _searchBanners$(
    searchParams: BannerSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Banner>> {
    return this.bannersService.search$(searchParams, returnAll).pipe()
  }

  /**
   * Search more than 200 banner with relative external data
   * @param searchParams - the search params
   * @param fields - the banner fields
   * @param listingData - the already loaded listing data
   * @returns the observable<BannersListingPage> for search banner
   */
  private _paginateBanners$(
    searchParams: BannerSearchParams,
  ): Observable<Page<Banner>> {
    const totalCount = searchParams.limit || this.bannersService.limit
    const pagesCount = Math.ceil(totalCount / this.bannersService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.bannersService.limit,
        offset: i * this.bannersService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchBanners$(pageParams)),
      scan<Page<Banner>, Page<Banner>>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
        }),
        { totalCount: 0, data: [] },
      ),
      last(),
    )
  }

  notify(options: { code?: BannerNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseBannerNotification(options.code),
    )
  }

  async alert(code: BannerModalCode, banner?: Banner): Promise<ModalResponse> {
    const settings = parseBannerModal(code, banner)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
